// ─── AUTH & SESSION (Supabase password/OAuth + Firebase OTP token) ────
import { API_BASE } from './config';
import { initials } from './helpers';
import { getSupabase } from './supabase-client';
import { startOAuth as startOAuthFlow } from './oauth-flow';

export let SB_SESSION = null;
try { SB_SESSION = JSON.parse(localStorage.getItem('fb_session') || 'null'); } catch (e) {}

export function authHeaders() {
  const h = { 'Content-Type': 'application/json' };
  if (SB_SESSION?.access_token) {
    h.Authorization = `Bearer ${SB_SESSION.access_token}`;
  }
  return h;
}

export function setSession(s) {
  SB_SESSION = s;
  if (s?.access_token) {
    try { localStorage.setItem('fb_session', JSON.stringify(s)); } catch (e) {}
  } else {
    try { localStorage.removeItem('fb_session'); } catch (e) {}
  }
}

/** Clear stale JWT and notify App to return to login. */
export function clearExpiredSession() {
  setSession(null);
  try { window.dispatchEvent(new CustomEvent('fb-session-expired')); } catch (_) { /* SSR */ }
}

export async function ensureFreshToken() {
  if (!SB_SESSION?.access_token) return;
  if (SB_SESSION.auth_provider === 'firebase' || SB_SESSION.auth_provider === 'legacy') return;
  if (!SB_SESSION.refresh_token) return;
  if (SB_SESSION.expires_at && Date.now() < SB_SESSION.expires_at - 60000) return;
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_KEY;
  if (!url || !key) return;
  try {
    const res = await fetch(`${url.replace(/\/$/, '')}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { apikey: key, 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: SB_SESSION.refresh_token }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.access_token) return;
    setSession({
      access_token: json.access_token,
      refresh_token: json.refresh_token || SB_SESSION.refresh_token,
      expires_at: Date.now() + ((json.expires_in || 3600) * 1000),
      auth_provider: 'supabase',
    });
  } catch (_) { /* ignore */ }
}

export async function apiPost(path, body) {
  await ensureFreshToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) {
    clearExpiredSession();
    throw new Error(json.error || 'Invalid or expired session');
  }
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

export async function apiGet(path) {
  await ensureFreshToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) {
    clearExpiredSession();
    throw new Error(json.error || 'Invalid or expired session');
  }
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

export async function fetchCounters() {
  const res = await apiGet('/dashboard/counters');
  return (res && res.data) || {};
}

/** Monotonic portal revision — cheap poll for cross-user sync. */
export async function fetchSyncRevision() {
  const res = await apiGet('/dashboard/sync-revision');
  const rev = res?.data?.revision;
  return rev == null ? null : Number(rev);
}

function mapAppUser(u) {
  return {
    id: u.id,
    name: u.name,
    role: u.role,
    avatar: u.avatar || initials(u.name),
    color: u.color || '#0B1F3A',
    email: u.email,
    phone: u.phone,
    mustChangePassword: !!(u.mustChangePassword || u.must_change_password),
  };
}

function storeSession(json) {
  setSession({
    access_token: json.access_token,
    refresh_token: json.refresh_token,
    expires_at: json.expires_at || Date.now() + ((json.expires_in || 3600) * 1000),
    auth_provider: json.auth_provider || 'supabase',
  });
}

export const Auth = {
  async login(identifier, password) {
    let res;
    try {
      res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: String(identifier || '').trim(), password }),
      });
    } catch {
      return { error: 'Cannot reach the server. Check your internet connection.' };
    }
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.access_token && json.user) {
      storeSession(json);
      return { user: mapAppUser(json.user) };
    }
    return { error: json.error || (res.ok ? 'Login failed' : `Login failed (${res.status})`) };
  },

  async signUp(payload) {
    const res = await fetch(`${API_BASE}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.error) return { error: json.error || 'Sign up failed' };
    if (json.access_token && json.user) {
      storeSession(json);
      return { user: mapAppUser(json.user), companyId: json.companyId };
    }
    return { ok: true, user: json.user ? mapAppUser(json.user) : null, companyId: json.companyId };
  },

  async resetPassword(resetToken, newPassword) {
    const res = await fetch(`${API_BASE}/auth/forgot/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resetToken, newPassword }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.error) return { error: json.error || 'Could not reset password' };
    return { ok: true, message: json.message || 'Password reset successfully' };
  },

  async changePassword(resetToken, newPassword) {
    const res = await fetch(`${API_BASE}/auth/change-password/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resetToken, newPassword }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.error) return { error: json.error || 'Could not change password' };
    return { ok: true, message: json.message || 'Password changed successfully' };
  },

  async logout() {
    const sb = getSupabase();
    if (sb && SB_SESSION?.auth_provider === 'supabase') {
      await sb.auth.signOut().catch(() => {});
    }
    setSession(null);
    localStorage.removeItem('fb_user');
    localStorage.removeItem('fb_view');
  },

  async deleteAccount({ confirmEmail } = {}) {
    const res = await fetch(`${API_BASE}/auth/delete-account`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ confirmEmail }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.error) return { ok: false, error: json.error || 'Could not delete account' };
    await Auth.logout();
    return { ok: true };
  },

  async startOAuth(provider, mode = 'login') {
    return startOAuthFlow(provider, mode);
  },

  async completeOAuth({ accessToken, refreshToken, expiresIn, expiresAt, provider, mode, signup }) {
    const res = await fetch(`${API_BASE}/auth/oauth/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accessToken,
        refreshToken,
        expiresIn,
        expiresAt,
        provider,
        mode,
        signup,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.error) return { error: json.error || 'Sign-in failed' };
    if (json.needsSignupProfile) {
      return {
        needsSignupProfile: true,
        profile: json.profile,
        oauth: { accessToken, refreshToken, expiresIn, expiresAt, provider },
      };
    }
    if (json.access_token && json.user) {
      storeSession(json);
      return { user: mapAppUser(json.user), companyId: json.companyId };
    }
    return { error: 'Sign-in failed' };
  },

  async completeOAuthSignup({ oauth, name, email, phone, companyName, companyType, provider }) {
    return Auth.completeOAuth({
      accessToken: oauth.accessToken,
      refreshToken: oauth.refreshToken,
      expiresIn: oauth.expiresIn,
      expiresAt: oauth.expiresAt,
      provider: provider || oauth.provider,
      mode: 'signup',
      signup: { name, email, phone, companyName, companyType },
    });
  },
};
